import React, { createContext, useContext, useState, useEffect } from 'react';
import crmAuthService, {
  decodeCrmToken,
  isCrmTokenExpired,
  type CrmUnidade,
  type CrmUnitSelectionPending,
} from '@/services/auth/crmAuthService';
import type { User } from '@/types';

const CRM_TOKEN_KEY = 'crmToken';
const CRM_LOGIN_KEY = 'crmLogin';

interface LoginResult {
  success: boolean;
  user?: User;
  error?: string;
  requiresUnitSelection?: boolean;
  unidades?: CrmUnidade[];
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  error: string | null;
  crmToken: string | null;
  login: (username: string, password: string) => Promise<LoginResult>;
  selectUnidade: (idUnidadeOperacional: number) => Promise<{ success: boolean; user?: User; error?: string }>;
  register: (userData: unknown) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  updateProfile: (profileData: Partial<User>) => Promise<{ success: boolean; user?: User; error?: string }>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
  resetPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

/** Monta o User exposto pelo app a partir dos campos seguros do token do CRM.
 *  Nunca inclui `host` (connection string do banco — ver aviso em crmAuthService.ts).
 *  O JWT do CRM não carrega o `login` — só o `id` numérico —, por isso ele vem à parte
 *  (da resposta de login, ou de `sessionStorage` na restauração de sessão). */
function buildUserFromToken(token: string, login: string): User | null {
  const payload = decodeCrmToken(token);
  if (!payload) return null;
  return {
    id: String(payload.id ?? ''),
    username: login,
    name: login,
    role: typeof payload.perfilEmpresa === 'string' ? payload.perfilEmpresa : undefined,
  };
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [crmToken, setCrmToken] = useState<string | null>(null);
  const [pendingUnitSelection, setPendingUnitSelection] = useState<CrmUnitSelectionPending | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = sessionStorage.getItem(CRM_TOKEN_KEY);
    const login = sessionStorage.getItem(CRM_LOGIN_KEY) ?? '';
    if (token && !isCrmTokenExpired(token)) {
      setCrmToken(token);
      setUser(buildUserFromToken(token, login));
    } else if (token) {
      sessionStorage.removeItem(CRM_TOKEN_KEY);
      sessionStorage.removeItem(CRM_LOGIN_KEY);
    }
    setLoading(false);
  }, []);

  const login = async (login: string, senha: string): Promise<LoginResult> => {
    try {
      setLoading(true);
      setError(null);
      const result = await crmAuthService.login(login, senha);

      if (result.requiresUnitSelection) {
        setPendingUnitSelection({
          partialToken: result.partialToken,
          userId: result.userId,
          login: result.login,
          unidades: result.unidades,
        });
        return { success: false, requiresUnitSelection: true, unidades: result.unidades };
      }

      sessionStorage.setItem(CRM_TOKEN_KEY, result.session.token);
      sessionStorage.setItem(CRM_LOGIN_KEY, result.session.login);
      setCrmToken(result.session.token);
      const loggedUser = buildUserFromToken(result.session.token, result.session.login);
      setUser(loggedUser);
      return { success: true, user: loggedUser ?? undefined };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao fazer login';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const selectUnidade = async (idUnidadeOperacional: number) => {
    if (!pendingUnitSelection) {
      return { success: false, error: 'Nenhuma seleção de unidade pendente' };
    }
    try {
      setLoading(true);
      setError(null);
      const { session } = await crmAuthService.selectUnidade(pendingUnitSelection, idUnidadeOperacional);
      sessionStorage.setItem(CRM_TOKEN_KEY, session.token);
      sessionStorage.setItem(CRM_LOGIN_KEY, session.login);
      setCrmToken(session.token);
      const loggedUser = buildUserFromToken(session.token, session.login);
      setUser(loggedUser);
      setPendingUnitSelection(null);
      return { success: true, user: loggedUser ?? undefined };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao selecionar unidade';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const register = async () => {
    setError('Registro não disponível. Entre em contato com o administrador.');
    return { success: false, error: 'Registro não disponível' };
  };

  const logout = async () => {
    sessionStorage.removeItem(CRM_TOKEN_KEY);
    sessionStorage.removeItem(CRM_LOGIN_KEY);
    setCrmToken(null);
    setUser(null);
    setPendingUnitSelection(null);
    setError(null);
  };

  const updateProfile = async (profileData: Partial<User>) => {
    try {
      setLoading(true);
      setError(null);
      const updatedUser = user ? { ...user, ...profileData } : null;
      setUser(updatedUser);
      return { success: true, user: updatedUser ?? undefined };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async () => {
    setError('Alteração de senha não disponível. Entre em contato com o administrador.');
    return { success: false, error: 'Alteração de senha não disponível' };
  };

  const resetPassword = async () => {
    setError('Recuperação de senha não disponível. Entre em contato com o administrador.');
    return { success: false, error: 'Recuperação de senha não disponível' };
  };

  const hasPermission = (_permission: string) => !!user;
  const hasRole = (_role: string) => !!user;

  const value: AuthContextValue = {
    user,
    loading,
    error,
    crmToken,
    login,
    selectUnidade,
    register,
    logout,
    updateProfile,
    changePassword,
    resetPassword,
    hasPermission,
    hasRole,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
